import cx from "clsx"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./InputField.module.css"

type Props = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  /** A value to read, not to enter: it can be reached and copied, not changed. */
  readOnly?: boolean
  error?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  // Error styling without a message of its own — for a field that is one part of
  // a group, where the message belongs to the group rather than to each part.
  invalid?: boolean
  required?: boolean
  type?: InputType
  name?: string
  autoComplete?: string
  inputMode?: "numeric" | "decimal" | "tel"
  maxLength?: number
}

type InputType =
  "text" | "email" | "tel" | "number" | "password" | "date" | "datetime-local"

/** Browsers leave `required` out of these inputs' accessibility tree, so their label says it. */
const REQUIRED_UNANNOUNCED: ReadonlySet<InputType> = new Set([
  "date",
  "datetime-local",
])

export function InputField({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  readOnly,
  error,
  hint,
  invalid,
  required,
  type = "text",
  name,
  autoComplete,
  inputMode,
  maxLength,
}: Props) {
  return (
    <div className={cx(styles.field, { [styles.error]: error || invalid })}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span aria-hidden="true" className={styles.required}>
            {" "}
            *
          </span>
        )}
        {required && REQUIRED_UNANNOUNCED.has(type) && (
          <>
            {" "}
            <span className={styles.srOnly}>(required)</span>
          </>
        )}
      </label>
      {hint && <FieldHint id={`${id}-hint`}>{hint}</FieldHint>}
      <input
        id={id}
        name={name ?? id}
        type={type}
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        required={required}
        aria-invalid={!!error || !!invalid}
        aria-describedby={describedBy(
          hint && `${id}-hint`,
          error && `${id}-error`,
        )}
      />
      {error && (
        <span
          id={`${id}-error`}
          className={styles.errorMessage}
          aria-live="polite"
        >
          {error}
        </span>
      )}
    </div>
  )
}
